import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ClipboardCopyIcon } from "@heroicons/react/outline";
import classNames from "classnames";
import { format, formatDistanceToNowStrict } from "date-fns";
import { DateTime } from "luxon";
import { ComponentType, Fragment, useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { CellProps } from "react-table";
import ReactTooltip from "react-tooltip";

import {
  SlideAdmin,
  SlideAdminList,
  AdminCaseList,
  Stain,
  SlideFlag,
  LabOrder,
  LabStainList,
  Case,
  CaseSlideAdmin,
} from "pwapi";

import { SlideStatusChange } from "custom-models";

import { setAdminLastSlideViewed } from "shared/actions/admin/adminSlidesActions";
import { StainsState } from "shared/reducers/frontLoaderReducers";
import { splitFlagReason } from "shared/utils/labOrderUtils";
import { printSlidesLink } from "shared/utils/printUtils";
import { createCompareAdminSlides, slideLabel } from "shared/utils/slideUtils";
import { procedureEnumToLabel } from "shared/utils/specimenUtils";
import { getStainByLabStainId } from "shared/utils/stainUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { UnderlineToggleButton } from "components/customInputs/buttons/ToggleButton";
import { useAppSelector } from "store/hooks";

import { SlidesTableProps } from "./SlidesTable";
import { SlideFlagIcon } from "../../viewer/SlideFlagIcon";
import { extractBasePathname, getTimeDifference } from "../adminUtils";
import ReactTableExpandableCell from "../table/ReactTableExpandableCell";

// TODO: extract a "CasesAdmin" (note plural) slide from OpenAPI
export const getSlideStatusChange = (
  slide: SlideAdminList | SlideAdmin | AdminCaseList["slides"][0]
) => {
  // TODO: Extracted from within the cell, but could be cleaner
  let statusChanges: SlideStatusChange[] =
    (slide as SlideAdmin).status_changes || [];
  if (statusChanges.length) {
    // Get the latest status change date
    // Or the only date in the list
    const statusChange: SlideStatusChange =
      statusChanges.length > 1
        ? statusChanges.reduce((dateA, dateB) =>
            new Date(dateA.created_at || "") > new Date(dateB.created_at || "")
              ? dateA
              : dateB
          )
        : statusChanges[0];
    return statusChange;
  }
};

const Flags = ({
  flags,
  labOrders,
  stains,
  labStains,
  showIcon = false,
}: {
  flags: SlideFlag[];
  labOrders?: LabOrder[];
  stains?: StainsState;
  labStains: LabStainList;
  showIcon?: boolean;
}) => {
  //TODO: remove this and create a subtable for the flags (currently only one subtable can exist at a time)
  const [isOpen, setIsOpen] = useState<boolean>(false);
  return (
    <div className="Cell__layout Cell__layout--column">
      <span
        className="cell__icon Cell__layout Cell__layout--center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <SlideFlagIcon />
        {isOpen ? (
          <FontAwesomeIcon icon="angle-down" fontSize={"12px"} />
        ) : (
          <FontAwesomeIcon icon="angle-right" fontSize={"12px"} />
        )}
      </span>
      {isOpen && (
        <span className="Cell__layout Cell__layout--column SlideFlag__details">
          {flags
            .filter((flag) => flag.reason)
            .map((flag) => {
              const labOrder = labOrders?.find((lo) => lo.id === flag.order_id);
              return (
                <Fragment key={flag.id}>
                  <span className="Cell__layout Cell__layout--center">
                    {showIcon && <SlideFlagIcon />}
                    {splitFlagReason(flag.reason)}
                  </span>
                  {labOrder && (
                    <small>
                      Order:{" "}
                      {
                        getStainByLabStainId(
                          labOrder?.lab_stain_id || 0,
                          stains,
                          labStains
                        )?.name
                      }
                      <br />
                    </small>
                  )}
                  {flag.note && (
                    <small className="Cell__layout--maxWidth">
                      "{flag.note}"<br />
                    </small>
                  )}
                  <small>
                    {flag.user &&
                      `${flag.user?.first_name} ${flag.user?.last_name}`}{" "}
                    &mdash;{" "}
                    {flag.created_at &&
                      `${formatDistanceToNowStrict(new Date(flag.created_at), {
                        addSuffix: true,
                      })}`}
                  </small>
                </Fragment>
              );
            })}
        </span>
      )}
    </div>
  );
};

const SlidesColumns =
  (
    Component: ComponentType<SlidesTableProps>,
    options?: {
      displayCaseInfo?: boolean;
      displayClipboardButton?: boolean;
      hideSlideRetention?: boolean;
      setLastViewed?: boolean;
      showPrintLabels?: boolean;
      adminPrinterType?: "zebra" | "espo";
    }
  ) =>
  (props: SlidesTableProps) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const stains = useAppSelector(({ stains }) => stains);
    const labStains = useAppSelector(({ labStains }) => labStains);

    const setLastViewed = (slide_id: number) => {
      options?.setLastViewed && dispatch(setAdminLastSlideViewed(slide_id));
    };

    const compareSlides = createCompareAdminSlides(stains);

    const sortType = useCallback(
      (labelA: CellProps<SlideAdminList>, labelB: CellProps<SlideAdminList>) =>
        compareSlides(labelA.original, labelB.original),
      []
    );

    const columns = useMemo(
      () => [
        {
          id: "barcode",
          Cell: ({ row }: CellProps<SlideAdminList>) => {
            return (
              <div
                className={classNames("cell__icon", "slideIcon--barcode", {
                  "cell__icon--expanded": row.isExpanded,
                })}
                onClick={() => {
                  row.toggleRowExpanded();
                }}
              >
                <FontAwesomeIcon icon="qrcode" />
              </div>
            );
          },
        },
        {
          accessor: "flag",
          Cell: ({ row }: CellProps<SlideAdminList>) => {
            if (row.original.flags?.length === 0) return <></>;

            return (
              <Flags
                flags={row.original.flags || []}
                labOrders={row.original.lab_orders}
                stains={stains}
                labStains={labStains}
                showIcon
              />
            );
          },
        },
        ...(options?.displayCaseInfo
          ? [
              {
                Header: "Case Accession",
                accessor: "accession",
                Cell: ({ row }: { row: { original: SlideAdminList } }) => {
                  return <>{row.original.case?.accession}</>;
                },
              },
            ]
          : []),
        {
          Header: "Slide Accession",
          accessor: "accession_number",
        },
        {
          Header: "Created At",
          accessor: "created_at",
          Cell: ({ row }: CellProps<SlideAdminList>) => {
            return row.original.created_at ? (
              <>{format(new Date(row.original.created_at), "MM/dd/yyyy")}</>
            ) : (
              <></>
            );
          },
        },
        {
          Header: "Received Date",
          accessor: "received_date",
          Cell: ({ row }: CellProps<SlideAdminList>) => {
            if (row.original.specimen && row.original.specimen.received_date) {
              return getTimeDifference(
                new Date(row.original.specimen.received_date)
              );
            }
            return <></>;
          },
        },
        {
          Header: "Uploaded Date",
          accessor: "uploaded_date",
          Cell: ({ row }: CellProps<SlideAdminList | CaseSlideAdmin>) => {
            if (
              "upload_completed_at" in row.original &&
              row.original.upload_completed_at
            )
              return (
                <>
                  {DateTime.fromISO(row.original.upload_completed_at).toFormat(
                    "MM/dd/yyyy"
                  )}
                </>
              );

            const statusChanges = row.original.status_changes;
            if (!statusChanges) return <></>;

            const uploadDateIndex = statusChanges.findIndex(
              (status) => status.new_status === "uploaded"
            );
            if (uploadDateIndex >= 0) {
              const uploadDate = new Date(
                statusChanges[uploadDateIndex].created_at || ""
              );
              return <>{format(uploadDate, "MM/dd/yyyy")}</>;
            }
            return <></>;
          },
        },
        {
          Header: "SID",
          accessor: "label",
          sortType,
          Cell: ({ row }: CellProps<SlideAdminList>) => (
            <span className="cell__content--noWrap">
              {slideLabel(
                row.original,
                row.original.block_obj,
                row.original.specimen,
                stains[row.original.stain_id || 0]?.category ===
                  Stain.category.ROUTINE
                  ? undefined
                  : stains[row.original.stain_id || 0]
              )}
            </span>
          ),
        },
        {
          Header: "Stain",
          accessor: "stain",
          Cell: ({ row }: CellProps<SlideAdminList>) => (
            <span className="cell__content--noWrap">
              {stains[row.original.stain_id || 0]?.name}
              {row.original.is_control && " CONTROL"}
              {row.original.is_rescan && " (Rescan)"}
            </span>
          ),
        },

        {
          Header: "Specimen Procedure",
          accessor: "specimen_procedure",
          Cell: ({ row }: CellProps<SlideAdminList>) => (
            <>
              {row.original.case?.source === Case.source.WPL
                ? row.original.specimen?.biopsy_type
                : procedureEnumToLabel(row.original.specimen?.procedure)}
            </>
          ),
        },

        {
          Header: "Status",
          accessor: "status",
          Cell: ({ row, value }: CellProps<SlideAdminList>) => {
            return (
              <span className="cell__content--noWrap">
                {value === "manual-review" ? (
                  <ReactTableExpandableCell
                    row={row}
                    expandedColor="var(--blue)"
                  >
                    <UnderlineToggleButton
                      title="manual review slide"
                      isToggled={row.isExpanded}
                      onClick={() => {
                        row.toggleRowExpanded();
                      }}
                    >
                      Manual Review
                    </UnderlineToggleButton>
                  </ReactTableExpandableCell>
                ) : (
                  value
                )}
              </span>
            );
          },
        },
        {
          Header: "Status Age",
          accessor: "status_age",
          Cell: ({ row }: CellProps<SlideAdminList>) => {
            const statusChange = getSlideStatusChange(row.original);
            if (statusChange && statusChange.created_at) {
              return getTimeDifference(new Date(statusChange.created_at));
            }
            return <></>;
          },
        },
        {
          Header: "QC Status",
          accessor: "qc_status",
          Cell: ({ row, value }: CellProps<SlideAdminList>) => {
            return value || row.original.qc_notes ? (
              <div
                className="Cell__layout Cell__layout--column"
                data-testid="qc_note"
              >
                <span className="Cell__layout Cell__layout--center">
                  <FontAwesomeIcon
                    icon="sticky-note"
                    className="slideIcon--note"
                  />
                  {value}
                </span>
                {row.original.qc_notes && (
                  <small className="Cell__layout--maxWidth">
                    {row.original.qc_notes}
                  </small>
                )}
              </div>
            ) : null;
          },
        },
        {
          Header: "Actions",
          disableSortBy: true,
          minWidth: options?.displayCaseInfo ? "20em" : "14em",
          sticky: "right",
          Cell: ({ row }: CellProps<SlideAdminList>) => {
            const pathname = extractBasePathname(location.pathname);
            const slideEditUrl = location.pathname.includes("/slides")
              ? `${pathname}${row.original.id}`
              : pathname === "/admin/"
              ? `${pathname}cases/slides/${row.original.id}`
              : `${pathname}slides/${row.original.id}`;
            return (
              <div
                className="cell__content--horizontal cell__content--actions"
                style={{ gap: "0.5em" }}
              >
                {options?.showPrintLabels && row.original.case?.id && (
                  <Link
                    to={printSlidesLink(
                      row.original.case.id,
                      row.original,
                      props.adminCase &&
                        "accessioning_lab" in props.adminCase &&
                        props.adminCase?.accessioning_lab?.settings
                          ?.admin_zebra_print?.active
                        ? "zebra"
                        : undefined
                    )}
                    target="_blank"
                    rel="opener"
                    className="PWTable__actions__link cell__content--noWrap"
                  >
                    Print "
                    {slideLabel(
                      row.original,
                      row.original.block_obj,
                      row.original.specimen,
                      stains[row.original.stain_id || 0]?.category ===
                        Stain.category.ROUTINE
                        ? undefined
                        : stains[row.original.stain_id || 0]
                    )}
                    " Label <FontAwesomeIcon icon="print" />
                  </Link>
                )}
                {options?.displayClipboardButton && (
                  <>
                    <ReactTooltip
                      id={`copy-slide-clipboard-${row.original.id}`}
                      place="top"
                      backgroundColor="var(--grey)"
                      effect="solid"
                      delayHide={250}
                      afterShow={() => {
                        navigator.clipboard
                          .writeText(`slide-${row.original.id}`)
                          .then(() =>
                            console.log(`Copied slide-${row.original.id}`)
                          )
                          .catch(() => {
                            console.error(
                              `Error copying slide-${row.original.id}`
                            );
                          });
                      }}
                    />
                    <button
                      data-for={`copy-slide-clipboard-${row.original.id}`}
                      data-event="click"
                      data-event-off="mouseout"
                      data-tip={`Copied slide-${row.original.id}`}
                      className="PWTable__actions__button pwButton--grey"
                      title={`Copy slide-${row.original.id}`}
                    >
                      <ClipboardCopyIcon height={20} />
                    </button>
                  </>
                )}
                <Link
                  to={{
                    pathname: slideEditUrl,
                    search: location.search,
                  }}
                  onClick={() => {
                    setLastViewed(row.original.id || 0);
                  }}
                >
                  <PathologyWatchButton size="small">Edit</PathologyWatchButton>
                </Link>
                {options?.displayCaseInfo && row.original.case?.id && (
                  <Link
                    to={`/admin/cases/${row.original.case?.id}/`}
                    state={{
                      label: "Slides",
                      prevUrl: `${location.pathname}${location.search}`,
                    }}
                  >
                    <PathologyWatchButton size="small">
                      Go to Case&nbsp;&rarr;
                    </PathologyWatchButton>
                  </Link>
                )}
                <Link
                  to={
                    row.original.case?.id
                      ? `/cases/${row.original.case?.id}/slides/${row.original.id}`
                      : `/slides/${row.original.id}`
                  }
                  onClick={() => {
                    setLastViewed(row.original.id || 0);
                  }}
                  target="_blank"
                >
                  <PathologyWatchButton size="small">
                    View&nbsp;&rarr;
                  </PathologyWatchButton>
                </Link>
              </div>
            );
          },
        },
      ],
      [location]
    );

    return <Component columns={columns} {...props} />;
  };

export default SlidesColumns;
